import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { Card, Input, Button, Alert, Spinner, MapPreview, useIsMobile } from '../components/ui';
import LocationSelect from '../components/ui/LocationSelect';
import PageLayout from '../components/layout/PageLayout';
import api from '../utils/api';

export default function NewCamp() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [checking, setChecking] = useState(true);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [locating, setLocating] = useState(false);
  const [locMessage, setLocMessage] = useState(null);
  const [coords, setCoords] = useState(null);
  const [place, setPlace] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', venue_name: '', address: '',
    camp_date: '', end_time: '', slots_total: '50', contact_name: '', contact_phone: '',
  });

  useEffect(() => {
    api.get('/organizations/me')
      .then(r => setOrg(r.data))
      .catch(e => { if (e.response?.status === 404) navigate('/org/setup'); })
      .finally(() => setChecking(false));
  }, [navigate]);

  const set = k => e => { setError(''); setForm(f => ({ ...f, [k]: e.target.value })); };

  // Coordinates are what put the camp on a map and drive the donor's
  // "Directions" link. Pinning by GPS only makes sense when the form is being
  // filled at the venue; otherwise the server geocodes the address on save.
  const pinCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocMessage({ variant: 'error', text: "Your browser can't share a location. We'll place the camp from the address instead." });
      return;
    }
    setLocating(true);
    setLocMessage(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setLocMessage({ variant: 'success', text: 'Pinned to your current position. Only use this if you\'re at the venue.' });
        setLocating(false);
      },
      () => {
        setLocMessage({ variant: 'warning', text: "Location access denied — we'll place the camp from the address you entered." });
        setLocating(false);
      },
      { timeout: 10000 }
    );
  };

  const clearPin = () => { setCoords(null); setLocMessage(null); };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.venue_name.trim() || !form.address.trim() || !form.camp_date) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!place) { setError('Select the country, region and city of the venue.'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/organizations/camps', {
        title: form.title.trim(),
        description: form.description.trim() || null,
        venue_name: form.venue_name.trim(),
        address: form.address.trim(),
        city_id: place.city_id,
        latitude: coords?.latitude ?? null,
        longitude: coords?.longitude ?? null,
        camp_date: new Date(form.camp_date).toISOString(),
        end_time: form.end_time ? new Date(form.end_time).toISOString() : null,
        slots_total: parseInt(form.slots_total, 10) || 50,
        contact_name: form.contact_name.trim() || null,
        contact_phone: form.contact_phone.trim() || null,
      });
      navigate('/org/dashboard');
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <PageLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}><Spinner /></div>
      </PageLayout>
    );
  }

  if (org && !org.is_verified) {
    return (
      <PageLayout>
        <Card>
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>⏳</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '8px' }}>
              Pending admin verification
            </div>
            <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', marginBottom: '20px', lineHeight: 1.6, maxWidth: '380px', margin: '0 auto 20px' }}>
              {org.name} hasn't been verified yet. You'll be able to create camps as soon as an admin approves your organization.
            </p>
            <Link to="/org/dashboard"><Button variant="secondary" size="sm">Back to dashboard</Button></Link>
          </div>
        </Card>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div style={{ animation: 'bb-fadeUp 0.5s ease both' }}>
      <div style={{ marginBottom: '16px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--gray-900)' }}>New donation camp</h1>
        <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', marginTop: '4px' }}>Donors will be able to browse and register once it's live.</p>
      </div>

      <Card style={{ maxWidth: isMobile ? '100%' : '560px' }}>
        <Input label="Camp title *" value={form.title} onChange={set('title')} placeholder="e.g. Mega Blood Donation Drive" />
        <Input label="Description (optional)" value={form.description} onChange={set('description')} />
        <Input label="Venue name *" value={form.venue_name} onChange={set('venue_name')} placeholder="e.g. Talkatora Stadium" />
        <Input label="Address *" value={form.address} onChange={set('address')} placeholder="Street, area, landmark — donors navigate to this" />
        <LocationSelect onChange={setPlace} required />

        {/* Map location */}
        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '6px' }}>Map location</div>
          <p style={{ fontSize: '12.5px', color: 'var(--gray-500)', lineHeight: 1.5, marginBottom: '10px' }}>
            We'll pin the camp from the address above so donors get a map and directions.
            Fill in a complete, specific address — or pin it exactly if you're standing at the venue.
          </p>
          {coords ? (
            <>
              <MapPreview
                place={{ ...coords, venue_name: form.venue_name }}
                height="160px"
                label="Check this pin on Maps"
                style={{ marginBottom: '8px' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: 'var(--gray-500)' }}>
                <span>{coords.latitude.toFixed(5)}, {coords.longitude.toFixed(5)}</span>
                <Button size="sm" variant="ghost" onClick={clearPin}>Remove pin</Button>
              </div>
            </>
          ) : (
            <Button size="sm" variant="secondary" loading={locating} onClick={pinCurrentLocation}>
              <MapPin size={13} /> Pin my current location
            </Button>
          )}
          {locMessage && <Alert variant={locMessage.variant} style={{ marginTop: '10px', marginBottom: 0 }}>{locMessage.text}</Alert>}
        </div>

        <Input label="Date & time *" type="datetime-local" value={form.camp_date} onChange={set('camp_date')} />
        <Input label="End time (optional)" type="datetime-local" value={form.end_time} onChange={set('end_time')} />
        <Input label="Total slots" type="number" min="1" value={form.slots_total} onChange={set('slots_total')} />
        <Input label="Contact name (optional)" value={form.contact_name} onChange={set('contact_name')} />
        <Input label="Contact phone (optional)" value={form.contact_phone} onChange={set('contact_phone')} />

        {error && <Alert variant="error">{error}</Alert>}

        <Button variant="primary" fullWidth loading={loading} onClick={handleSubmit}>
          Create camp
        </Button>
      </Card>
      </div>
    </PageLayout>
  );
}
